import {
  Alert,
  AlertIcon,
  Box,
  Checkbox,
  Divider,
  Flex,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useState } from "react";
import { HiOutlineCheck, HiOutlineX } from "react-icons/hi";

import { Button } from "../../../components";
import {
  AiInterviewQuestion,
  GuideQuestionAnalysis,
  GuideSuggestionItem,
  SelectedSuggestionInput,
} from "../../graphql";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";
import useApplyQuestionSuggestions from "./useApplyQuestionSuggestions";

type QuestionAnalysisPanelProps = {
  questionIndex: number;
  analysis: GuideQuestionAnalysis;
  onAccepted?: () => void;
};

type PreviewState = {
  original: AiInterviewQuestion;
  updated: AiInterviewQuestion;
};

export const QuestionAnalysisPanel: React.FC<QuestionAnalysisPanelProps> = ({
  questionIndex,
  analysis,
  onAccepted,
}) => {
  const { setValue, getValues } = useAiInterviewerFormContext();
  const {
    applySuggestions,
    loading: applyLoading,
    error,
  } = useApplyQuestionSuggestions();

  // Track selected suggestions by their ID
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Preview state - holds both original and updated question for comparison
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);

  const handleToggleSuggestion = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const handleApply = useCallback(async () => {
    // Collect selected suggestions using flatMap
    const selectedSuggestions: SelectedSuggestionInput[] =
      analysis.suggestionGroups.flatMap((group) =>
        group.items
          .filter((item) => selectedIds.has(item.id))
          .map((item) => ({
            category: group.category,
            title: item.title,
            description: item.description,
          }))
      );

    if (selectedSuggestions.length === 0) {
      return;
    }

    // Capture original question from form before applying
    const formValues = getValues();
    const originalQuestion = formValues.questions[questionIndex];

    const result = await applySuggestions(questionIndex, selectedSuggestions);
    if (result) {
      setPreviewState({
        original: originalQuestion,
        updated: result,
      });
    }
  }, [
    analysis.suggestionGroups,
    applySuggestions,
    getValues,
    questionIndex,
    selectedIds,
  ]);

  const handleAccept = useCallback(() => {
    if (!previewState) return;

    // Update form with the new question values
    const fields = [
      "question",
      "type",
      "followups",
      "rubric",
      "qualificationCriteria",
      "summaryCriteria",
    ] as const;

    fields.forEach((key) => {
      const fieldPath = `questions.${questionIndex}.${key}` as const;
      if (key in previewState.updated) {
        setValue(fieldPath, (previewState.updated as any)[key], {
          shouldDirty: true,
        });
      }
    });

    // Clear preview state
    setPreviewState(null);
    setSelectedIds(new Set());

    // Notify parent to collapse this question's suggestions
    onAccepted?.();
  }, [previewState, questionIndex, setValue, onAccepted]);

  const handleCancel = useCallback(() => {
    setPreviewState(null);
    // Keep selectedIds so user can modify selections and re-apply
  }, []);

  const hasNoSuggestions = analysis.suggestionGroups.length === 0;
  const hasSelectedSuggestions = selectedIds.size > 0;

  // If showing preview
  if (previewState) {
    return (
      <PreviewChanges
        original={previewState.original}
        updated={previewState.updated}
        onAccept={handleAccept}
        onCancel={handleCancel}
      />
    );
  }

  // No suggestions
  if (hasNoSuggestions) {
    return (
      <Box p={4} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" color="gray.600" textAlign="center">
          No suggestions for this question.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Text fontWeight="600" fontSize="sm" color="gray.700" mb={3}>
        Suggestions
      </Text>
      {error && (
        <Alert status="error" mb={3} fontSize="sm" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}
      <VStack align="stretch" spacing={4}>
        {analysis.suggestionGroups.map((group, idx) => (
          <Box key={`${group.category}-${group.groupHeader}`}>
            <Text fontSize="sm" fontWeight="500" color="gray.700" mb={2}>
              {group.groupHeader}
            </Text>
            <VStack align="stretch" spacing={2} pl={2}>
              {group.items.map((item) => (
                <SuggestionItemRow
                  key={item.id}
                  item={item}
                  isSelected={selectedIds.has(item.id)}
                  onToggle={(checked) =>
                    handleToggleSuggestion(item.id, checked)
                  }
                />
              ))}
            </VStack>
            {idx < analysis.suggestionGroups.length - 1 && <Divider mt={3} />}
          </Box>
        ))}
      </VStack>
      <Flex justify="flex-end" mt={4}>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={handleApply}
          isLoading={applyLoading}
          isDisabled={!hasSelectedSuggestions || applyLoading}
        >
          Apply Selected ({selectedIds.size})
        </Button>
      </Flex>
    </Box>
  );
};

type SuggestionItemRowProps = {
  item: GuideSuggestionItem;
  isSelected: boolean;
  onToggle: (checked: boolean) => void;
};

const SuggestionItemRow: React.FC<SuggestionItemRowProps> = ({
  item,
  isSelected,
  onToggle,
}) => {
  return (
    <Box
      bg={isSelected ? "blue.50" : "white"}
      p={2}
      borderRadius="md"
      borderWidth="1px"
      borderColor={isSelected ? "blue.200" : "gray.200"}
    >
      <Flex align="flex-start" gap={2}>
        <Checkbox
          isChecked={isSelected}
          onChange={(e) => onToggle(e.target.checked)}
          size="sm"
          colorScheme="blue"
          mt="2px"
        />
        <Box cursor="pointer" onClick={() => onToggle(!isSelected)}>
          <Text fontSize="sm" fontWeight="500">
            {item.title}
          </Text>
          <Text fontSize="xs" color="gray.600" mt={0.5} wordBreak="break-word">
            {item.description}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

// --- Preview Component with Diff ---

type PreviewChangesProps = {
  original: AiInterviewQuestion;
  updated: AiInterviewQuestion;
  onAccept: () => void;
  onCancel: () => void;
};

const PreviewChanges: React.FC<PreviewChangesProps> = ({
  original,
  updated,
  onAccept,
  onCancel,
}) => {
  // Calculate which fields changed
  const changes = useMemo(() => {
    const result: {
      question?: { old: string; new: string };
      followups?: { old: string[]; new: string[] };
      rubric?: { old: string[]; new: string[] };
      qualificationCriteria?: { old: string; new: string };
      summaryCriteria?: { old: string; new: string };
    } = {};

    if (original.question !== updated.question) {
      result.question = { old: original.question, new: updated.question };
    }

    const oldFollowups = (original.followups ?? []).filter(
      (f): f is string => f != null
    );
    const newFollowups = (updated.followups ?? []).filter(
      (f): f is string => f != null
    );
    if (JSON.stringify(oldFollowups) !== JSON.stringify(newFollowups)) {
      result.followups = { old: oldFollowups, new: newFollowups };
    }

    const oldRubric = (original.rubric ?? []).filter(
      (r): r is string => r != null
    );
    const newRubric = (updated.rubric ?? []).filter(
      (r): r is string => r != null
    );
    if (JSON.stringify(oldRubric) !== JSON.stringify(newRubric)) {
      result.rubric = { old: oldRubric, new: newRubric };
    }

    // Only include criteria if there's actually a meaningful change
    // (not just both being empty/null)
    const oldQual = original.qualificationCriteria ?? "";
    const newQual = updated.qualificationCriteria ?? "";
    if (oldQual !== newQual && (oldQual || newQual)) {
      result.qualificationCriteria = { old: oldQual, new: newQual };
    }

    const oldSummary = original.summaryCriteria ?? "";
    const newSummary = updated.summaryCriteria ?? "";
    if (oldSummary !== newSummary && (oldSummary || newSummary)) {
      result.summaryCriteria = { old: oldSummary, new: newSummary };
    }

    return result;
  }, [original, updated]);

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <Box
      p={4}
      bg="green.50"
      borderRadius="md"
      borderColor="green.200"
      borderWidth="1px"
    >
      <Text fontWeight="600" fontSize="sm" color="green.700" mb={3}>
        Preview Changes
      </Text>
      {!hasChanges ? (
        <Text fontSize="sm" color="gray.600" mb={3}>
          No changes detected.
        </Text>
      ) : (
        <VStack align="stretch" spacing={3}>
          {changes.question && (
            <DiffField
              label="Question"
              oldValue={changes.question.old}
              newValue={changes.question.new}
            />
          )}
          {changes.followups && (
            <DiffListField
              label="Follow-ups"
              oldItems={changes.followups.old}
              newItems={changes.followups.new}
            />
          )}
          {changes.rubric && (
            <DiffRubricField
              oldRubric={changes.rubric.old}
              newRubric={changes.rubric.new}
            />
          )}
          {changes.qualificationCriteria && (
            <DiffField
              label="Qualification Criteria"
              oldValue={changes.qualificationCriteria.old}
              newValue={changes.qualificationCriteria.new}
            />
          )}
          {changes.summaryCriteria && (
            <DiffField
              label="Summary Criteria"
              oldValue={changes.summaryCriteria.old}
              newValue={changes.summaryCriteria.new}
            />
          )}
        </VStack>
      )}
      <Flex gap={2} justify="flex-end" mt={4}>
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<HiOutlineX />}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          colorScheme="green"
          leftIcon={<HiOutlineCheck />}
          onClick={onAccept}
          isDisabled={!hasChanges}
        >
          Accept Changes
        </Button>
      </Flex>
    </Box>
  );
};

// --- Diff Display Components ---

type DiffFieldProps = {
  label: string;
  oldValue: string;
  newValue: string;
};

const DiffField: React.FC<DiffFieldProps> = ({ label, oldValue, newValue }) => {
  const diffResult = useMemo(
    () => computeWordDiff(oldValue, newValue),
    [oldValue, newValue]
  );

  return (
    <Box>
      <Text fontSize="xs" fontWeight="500" color="gray.600" mb={1}>
        {label}
      </Text>
      <Box fontSize="sm" bg="white" p={2} borderRadius="sm" lineHeight="1.6">
        <RenderDiff parts={diffResult} />
      </Box>
    </Box>
  );
};

type DiffListFieldProps = {
  label: string;
  oldItems: string[];
  newItems: string[];
};

const DiffListField: React.FC<DiffListFieldProps> = ({
  label,
  oldItems,
  newItems,
}) => {
  // Find items that were removed, added, or changed
  const maxLen = Math.max(oldItems.length, newItems.length);
  const diffItems: Array<{ old?: string; new?: string; changed: boolean }> = [];

  for (let i = 0; i < maxLen; i++) {
    const oldItem = oldItems[i];
    const newItem = newItems[i];
    diffItems.push({
      old: oldItem,
      new: newItem,
      changed: oldItem !== newItem,
    });
  }

  return (
    <Box>
      <Text fontSize="xs" fontWeight="500" color="gray.600" mb={1}>
        {label}
      </Text>
      <VStack align="stretch" spacing={1}>
        {diffItems.map((item, idx) => (
          <Box
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            fontSize="sm"
            bg="white"
            p={2}
            borderRadius="sm"
            lineHeight="1.6"
          >
            {item.changed ? (
              <>
                {item.old && (
                  <Text
                    as="span"
                    bg="red.100"
                    color="red.600"
                    textDecoration="line-through"
                  >
                    {item.old}
                  </Text>
                )}
                {item.old && item.new && " "}
                {item.new && (
                  <Text as="span" bg="green.100" color="green.600">
                    {item.new}
                  </Text>
                )}
              </>
            ) : (
              <Text as="span">{item.new}</Text>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

type DiffRubricFieldProps = {
  oldRubric: string[];
  newRubric: string[];
};

const DiffRubricField: React.FC<DiffRubricFieldProps> = ({
  oldRubric,
  newRubric,
}) => {
  // Rubric items are ordered 4, 3, 2, 1
  const scores = [4, 3, 2, 1];

  // Check which tiers changed (trim to avoid false positives from whitespace)
  const changedTiers = scores
    .map((score, idx) => {
      const oldItem = oldRubric[idx] ?? "";
      const newItem = newRubric[idx] ?? "";
      return { score, oldItem, newItem };
    })
    .filter(({ oldItem, newItem }) => oldItem.trim() !== newItem.trim());

  if (changedTiers.length === 0) {
    return null;
  }

  return (
    <Box>
      <Text fontSize="xs" fontWeight="500" color="gray.600" mb={1}>
        Rubric
      </Text>
      <VStack align="stretch" spacing={1}>
        {changedTiers.map(({ score, oldItem, newItem }) => (
          <Box
            key={score}
            fontSize="sm"
            bg="white"
            p={2}
            borderRadius="sm"
            lineHeight="1.6"
          >
            <Text as="span" fontWeight="500" color="gray.600">
              {score}:
            </Text>{" "}
            <InlineDiff oldValue={oldItem} newValue={newItem} />
          </Box>
        ))}
      </VStack>
    </Box>
  );
};

type InlineDiffProps = {
  oldValue: string;
  newValue: string;
};

const InlineDiff: React.FC<InlineDiffProps> = ({ oldValue, newValue }) => {
  const diffResult = useMemo(
    () => computeWordDiff(oldValue, newValue),
    [oldValue, newValue]
  );

  return <RenderDiff parts={diffResult} />;
};

// --- Render diff parts ---

type RenderDiffProps = {
  parts: DiffPart[];
};

const RenderDiff: React.FC<RenderDiffProps> = ({ parts }) => {
  return (
    <>
      {parts.map((part, idx) => (
        <Text
          as="span"
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          bg={
            part.type === "removed"
              ? "red.100"
              : part.type === "added"
              ? "green.100"
              : undefined
          }
          textDecoration={part.type === "removed" ? "line-through" : undefined}
          color={
            part.type === "removed"
              ? "red.600"
              : part.type === "added"
              ? "green.600"
              : undefined
          }
        >
          {part.text}
        </Text>
      ))}
    </>
  );
};

// --- Word-based diff algorithm ---

type DiffPart = {
  type: "unchanged" | "removed" | "added";
  text: string;
};

/**
 * Compute a word-level diff between two strings.
 * Groups consecutive changes into spans for cleaner display.
 */
function computeWordDiff(oldText: string, newText: string): DiffPart[] {
  // Tokenize into words (split on whitespace boundaries)
  const oldWords = tokenize(oldText);
  const newWords = tokenize(newText);

  // Compute LCS to find common words
  const lcs = longestCommonSubsequence(oldWords, newWords);
  const rawParts: DiffPart[] = [];

  let oldIdx = 0;
  let newIdx = 0;
  let lcsIdx = 0;

  while (oldIdx < oldWords.length || newIdx < newWords.length) {
    // Collect all removed words until we hit the next LCS word
    const removedWords: string[] = [];
    while (
      oldIdx < oldWords.length &&
      (lcsIdx >= lcs.length || oldWords[oldIdx] !== lcs[lcsIdx])
    ) {
      removedWords.push(oldWords[oldIdx]);
      oldIdx += 1;
    }

    // Collect all added words until we hit the next LCS word
    const addedWords: string[] = [];
    while (
      newIdx < newWords.length &&
      (lcsIdx >= lcs.length || newWords[newIdx] !== lcs[lcsIdx])
    ) {
      addedWords.push(newWords[newIdx]);
      newIdx += 1;
    }

    // Add removed span (all removed words together)
    if (removedWords.length > 0) {
      rawParts.push({ type: "removed", text: removedWords.join(" ") });
    }

    // Add added span (all added words together)
    if (addedWords.length > 0) {
      rawParts.push({ type: "added", text: addedWords.join(" ") });
    }

    // Add the common word
    if (lcsIdx < lcs.length) {
      rawParts.push({ type: "unchanged", text: lcs[lcsIdx] });
      oldIdx += 1;
      newIdx += 1;
      lcsIdx += 1;
    }
  }

  // Post-process: merge adjacent same-type parts and add proper spacing
  return mergeAndSpaceParts(rawParts);
}

/**
 * Merge adjacent parts of the same type and add spaces between parts.
 */
function mergeAndSpaceParts(parts: DiffPart[]): DiffPart[] {
  if (parts.length === 0) return [];

  const result: DiffPart[] = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const prev = result[result.length - 1];

    // If same type as previous, merge them with a space
    if (prev && prev.type === part.type) {
      prev.text += ` ${part.text}`;
    } else if (prev) {
      // Add space before this part if there's a previous part
      // We add it to the previous "unchanged" part if possible for cleaner display
      if (prev.type === "unchanged") {
        prev.text += " ";
        result.push(part);
      } else if (part.type === "unchanged") {
        part.text = ` ${part.text}`;
        result.push(part);
      } else {
        // Both are changes - add space as separate unchanged part
        result.push({ type: "unchanged", text: " " });
        result.push(part);
      }
    } else {
      result.push(part);
    }
  }

  return result;
}

/**
 * Tokenize text into words (no whitespace tokens).
 */
function tokenize(text: string): string[] {
  return text.split(/\s+/).filter((w) => w.length > 0);
}

/**
 * Compute the longest common subsequence of two arrays.
 */
function longestCommonSubsequence(a: string[], b: string[]): string[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to find the LCS
  const result: string[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      result.unshift(a[i - 1]);
      i -= 1;
      j -= 1;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i -= 1;
    } else {
      j -= 1;
    }
  }

  return result;
}

export default QuestionAnalysisPanel;
